import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import {
  Box,
  Button,
  type ButtonProps,
  SxProps,
  Theme,
  Typography,
  type TypographyProps
} from "@mui/material";

import { homePageVehicleTypeAtom } from "@/src/state/homePageVehicleTypeAtom";
import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import DealerLogos from "@/src/components/atoms/DealerLogos";
import Testimonials from "@/src/components/atoms/Testimonials";
import { WholePageSpinner } from "@/src/components/organisms";

import HomePageArticleBlocks from "@/src/pages/consumer/HomePage/components/HomePageArticleBlock/HomePageArticleBlock";
import { HomePageDescription } from "@/src/pages/consumer/HomePage/components/HomePageDescription/HomePageDescription";
import { HomePageFAQList } from "@/src/pages/consumer/HomePage/components/HomePageFAQList/HomePageFAQList";
import HomePageHeader from "@/src/pages/consumer/HomePage/components/HomePageHeader/HomePageHeader";
import { HomePageInstructions } from "@/src/pages/consumer/HomePage/components/HomePageInstructions/HomePageInstructions";
import HomePageKatsastusBanner from "@/src/pages/consumer/HomePage/components/HomePageKatsastusBanner";
import { HomePageProps } from "@/src/pages/consumer/HomePage/HomePage";
import { CONSUMER_HOMEPAGE_PREFIX } from "@/src/pages/consumer/HomePage/homePage.constants";
import HomePageImageCarousel from "@/src/pages/consumer/HomePage/HomePageImageCarousel/HomePageImageCarousel";
import { onAuctionListingClick } from "@/src/pages/consumer/HomePage/homePageUtils";

export {
  CONSUMER_HOMEPAGE_PREFIX,
  HOMEPAGE_CONTAINER_STYLES
} from "@/src/pages/consumer/HomePage/homePage.constants";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import {
  ROUTES,
  getAuctionCreateRoute,
  getAuctionEditRoute
} from "@/src/constants/routes";
import {
  useGetAllAuctions,
  useGetDraftAuction
} from "@/src/hooks/consumerAuctions";
import { useAuctionCreationOnLogin } from "@/src/hooks/useAuctionCreationOnLogin";
import { useClearAuctionState } from "@/src/hooks/useClearAuctionState";
import { AuctionState, VehicleType, VerticalSource } from "@/src/types/types";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

const HOMEPAGE_PATH_BY_VEHICLE: Record<VehicleType, string> = {
  [VehicleType.CAR]: "/",
  [VehicleType.MOTO]: "/moto",
  [VehicleType.CARAVAN]: "/caravan"
};

const isOnHomepage = (pathname: string): boolean =>
  /^\/(moto|caravan)?$/.test(pathname);

const handleSelectedVehicleTypeMismatch = (
  selectedVehicleType: VehicleType,
  currentPath: string,
  navigate: (path: string) => void
): boolean => {
  const expectedPath = HOMEPAGE_PATH_BY_VEHICLE[selectedVehicleType];
  if (currentPath !== expectedPath) {
    navigate(expectedPath);
    return true;
  }
  return false;
};

const handlePathBasedVehicleType = (
  currentPath: string,
  selectedVehicleType: VehicleType | undefined,
  setVehicleType: (type: VehicleType) => void
): boolean => {
  const pathToType: Record<string, VehicleType> = {
    "/moto": VehicleType.MOTO,
    "/": VehicleType.CAR,
    "/caravan": VehicleType.CARAVAN
  };
  const vehicleType = pathToType[currentPath];
  if (vehicleType && selectedVehicleType === undefined) {
    setVehicleType(vehicleType);
    return true;
  }
  return false;
};

const syncPathWithPropsVehicleType = (
  propsVehicleType: VehicleType,
  currentPath: string,
  navigate: (path: string) => void,
  setVehicleType: (type: VehicleType) => void
): boolean => {
  const targetPath = HOMEPAGE_PATH_BY_VEHICLE[propsVehicleType];
  if (currentPath !== targetPath) {
    navigate(targetPath);
    setVehicleType(propsVehicleType);
    return true;
  }
  return false;
};

const toSxPropArray = (sx: SxProps<Theme> | undefined) => {
  if (sx == null) {
    return [];
  }
  if (Array.isArray(sx)) {
    return [...sx];
  }
  return [sx];
};

interface HomePageButtonProps {
  variant: "text" | "outlined" | "contained";
  color:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  buttonText: string;
  sx?: SxProps<Theme>;
  onClick: () => void;
  size?: ButtonProps["size"];
  textVariant?: TypographyProps["variant"];
  textComponent?: TypographyProps["component"];
  textSx?: SxProps<Theme>;
  abTestId?: string;
}

export const HomePageButton = (props: HomePageButtonProps) => {
  const {
    variant,
    color,
    buttonText,
    sx,
    onClick,
    size = "medium",
    textVariant = "h5",
    textComponent = "h2",
    textSx,
    abTestId
  } = props;

  return (
    <Button
      variant={variant || "contained"}
      color={color || "primary"}
      size={size}
      onClick={onClick}
      data-ab-test={abTestId}
      sx={[
        {
          boxShadow: 3
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <Typography
        component={textComponent}
        variant={textVariant}
        sx={[
          {
            margin: 0,
            padding: 0,
            cursor: "pointer"
          },
          ...toSxPropArray(textSx)
        ]}
      >
        {buttonText}
      </Typography>
    </Button>
  );
};

export const HomePageContent = (props: HomePageProps) => {
  const selectedVehicleType = useAtomValue(homePageVehicleTypeAtom);
  const vehicleType = selectedVehicleType ?? props.vehicleType;
  const { isAuthenticated, user, login } = useAuth();
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const setVehicleType = useSetAtom(homePageVehicleTypeAtom);
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const { isFetching: isLoadingUserAuctions, data: allAuctions } =
    useGetAllAuctions();
  const { data: draftAuctionData } = useGetDraftAuction();
  const activeAuction = userAuctionsBadges?.activeAuctionId;

  // Check if there's ANY draft auction (user can only have ONE draft at a time)
  const anyDraftAuction = useMemo(() => {
    if (!allAuctions) return undefined;
    return allAuctions.find(
      (auction) => auction.auctionState === AuctionState.DRAFT
    );
  }, [allAuctions]);

  // Handle Nettiauto/Nettimoto import flow
  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const regno = urlParams.get("regno");
    const source = urlParams.get("source");

    // Handle regno + source parameters
    if (
      regno &&
      (globalThis.location.pathname === ROUTES.CONSUMER.HOME ||
        globalThis.location.pathname === ROUTES.CONSUMER.HOME_MOTO ||
        globalThis.location.pathname === ROUTES.CONSUMER.HOME_CARAVAN)
    ) {
      // Determine source: use URL param if present, otherwise infer from route
      // If on /moto route, default to nettimoto; if on /caravan, default to nettiauto; else nettiauto
      const getSourceToUse = (): VerticalSource => {
        if (source === VerticalSource.Nettimoto)
          return VerticalSource.Nettimoto;
        if (source === VerticalSource.Nettiauto)
          return VerticalSource.Nettiauto;
        if (globalThis.location.pathname === ROUTES.CONSUMER.HOME_MOTO) {
          return VerticalSource.Nettimoto;
        }
        return VerticalSource.Nettiauto;
      };
      const sourceToUse = getSourceToUse();
      // Determine vehicle type from route or source
      const getImportVehicleType = (): VehicleType => {
        if (sourceToUse === VerticalSource.Nettimoto) return VehicleType.MOTO;
        if (globalThis.location.pathname === ROUTES.CONSUMER.HOME_CARAVAN) {
          return VehicleType.CARAVAN;
        }
        return VehicleType.CAR;
      };
      const importVehicleType = getImportVehicleType();

      // Preserve query params through navigation - don't clear them here
      // The useVerticalImport hook will handle the import and clear params after completion
      if (isAuthenticated) {
        // User is authenticated, redirect to private wizard with query params preserved
        // Use importVehicleType instead of current route's vehicleType
        const wizardPath = getAuthenticatedWizardPath(importVehicleType);
        // Navigate with query params preserved
        navigate(
          `${wizardPath}?regno=${encodeURIComponent(
            regno
          )}&source=${sourceToUse}`
        );
      } else {
        // User not authenticated, redirect to public wizard with query params preserved
        // Use importVehicleType instead of current route's vehicleType
        const wizardPath = getUnauthenticatedWizardPath(importVehicleType);
        // Navigate with query params preserved
        navigate(
          `${wizardPath}?regno=${encodeURIComponent(
            regno
          )}&source=${sourceToUse}`
        );
      }
    }
  }, [vehicleType, navigate, isAuthenticated]);

  const getAuthenticatedWizardPath = (vehicleType: VehicleType): string => {
    return getAuctionEditRoute(vehicleType);
  };

  const getUnauthenticatedWizardPath = (vehicleType: VehicleType): string => {
    return getAuctionCreateRoute(vehicleType);
  };

  const { createAuctionInBackend, processingAuction } =
    useAuctionCreationOnLogin();

  const { clearAllAuctionState } = useClearAuctionState();

  // Custom wrapper for onAuctionListingClick that clears atoms for new auctions
  const handleAuctionListingClick = (
    source: LoginSource,
    vehicleType: VehicleType
  ) => {
    // Clear atoms when creating a new auction (not when continuing draft)
    // Only clear if there's no draft at all
    if (isAuthenticated && !anyDraftAuction) {
      clearAllAuctionState();
    }

    // Pass the draft ID if it exists
    const draftAuctionIdForType = anyDraftAuction?.id;

    onAuctionListingClick(
      source,
      isAuthenticated,
      draftAuctionIdForType,
      activeAuction,
      navigate,
      user,
      sendDataToGTM,
      login,
      vehicleType
    );
  };

  let buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.start`;

  // Show "continue" if there's ANY draft (user can only have one draft at a time)
  if (anyDraftAuction) {
    buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.continueAuction`;
  }

  useEffect(() => {
    if (isLoadingUserAuctions) {
      return;
    }
    // Only create auction if there's no draft at all (user can only have ONE draft)
    if (isAuthenticated && !anyDraftAuction) {
      createAuctionInBackend();
    }
  }, [
    isAuthenticated,
    isLoadingUserAuctions,
    anyDraftAuction,
    createAuctionInBackend
  ]);

  useEffect(() => {
    if (!isOnHomepage(globalThis.location.pathname)) return;

    const draftVehicleType = draftAuctionData?.auctionItem.vehicle?.vehicleType;
    const currentPath = globalThis.location.pathname;

    // case 1: User explicitly selected vehicle type - ALWAYS respect their choice
    if (selectedVehicleType) {
      const didNavigate = handleSelectedVehicleTypeMismatch(
        selectedVehicleType,
        currentPath,
        navigate
      );
      if (didNavigate) return;
    }

    // case 2–3b: Set vehicle type from current path when no explicit selection
    if (
      handlePathBasedVehicleType(
        currentPath,
        selectedVehicleType,
        setVehicleType
      )
    ) {
      return;
    }

    // case 4: No selection and no draft - sync path with props.vehicleType
    const noSelectionNoDraft =
      selectedVehicleType === undefined && !draftVehicleType;
    if (
      noSelectionNoDraft &&
      syncPathWithPropsVehicleType(
        props.vehicleType,
        currentPath,
        navigate,
        setVehicleType
      )
    ) {
      return;
    }

    // case 5: Set vehicle type from draft when user hasn't explicitly selected
    if (selectedVehicleType === undefined && draftVehicleType) {
      setVehicleType(draftVehicleType);
    }
  }, [
    selectedVehicleType,
    navigate,
    draftAuctionData,
    setVehicleType,
    props.vehicleType
  ]);

  if (isLoadingUserAuctions || processingAuction) {
    return <WholePageSpinner />;
  }

  return (
    <Box>
      <HomePageHeader
        vehicleType={vehicleType}
        onAuctionListingClick={() =>
          handleAuctionListingClick(LoginSource.LANDING_PAGE_CTA1, vehicleType)
        }
        buttonText={buttonText}
      />
      <HomePageDescription vehicleType={vehicleType} />
      <HomePageKatsastusBanner vehicleType={vehicleType} />
      <HomePageImageCarousel />
      <DealerLogos vehicleType={vehicleType} />
      <HomePageInstructions
        carOnAuctionListingClick={() =>
          handleAuctionListingClick(
            LoginSource.LANDING_PAGE_CTA2,
            VehicleType.CAR
          )
        }
        motoOnAuctionListingClick={() =>
          handleAuctionListingClick(
            LoginSource.LANDING_PAGE_CTA2,
            VehicleType.MOTO
          )
        }
        caravanOnAuctionListingClick={() =>
          handleAuctionListingClick(
            LoginSource.LANDING_PAGE_CTA2,
            VehicleType.CARAVAN
          )
        }
        vehicleType={vehicleType}
      />
      <Testimonials />
      <HomePageArticleBlocks />
      <HomePageFAQList />
    </Box>
  );
};
